import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import WizardLayout from '../../../components/wizardLayout/WizardLayout'
import Billing from '../../../components/authenticated/account/billing/Billing'
import ProfileDetails from '../../../components/authenticated/account/profile/profileComponents/ProfileDetails'
import TermsConsent from '../../../components/authenticated/account/termsConsent/TermsConsent'

import { useAuth } from '../../../contexts/AuthContext'
import { useProfileContext } from '../../../contexts/ProfileContext'
import {
  CompletionMode,
  CompletionRedirectState,
} from '../../../types/account/accountSetup'
import { getFromToken, JwtKeys } from '../../../utils/jwtUtils'
import { hasCompleteBillingAddress } from '../../../utils/address/address'
import {
  hasAcceptedTerms,
  hasCompletePersonalDetails,
} from '../../../utils/profile/profile'

enum SetupStep {
  PERSONAL = 'personal',
  TERMS = 'terms',
  BILLING = 'billing',
}

const isCompletionRedirectState = (
  value: unknown
): value is CompletionRedirectState => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const state = value as Partial<CompletionRedirectState>

  return (
    Object.values(CompletionMode).includes(state.mode as CompletionMode) &&
    (state.returnTo === undefined || typeof state.returnTo === 'string')
  )
}

const AccountSetup = () => {
  const { t } = useTranslation('account')
  const location = useLocation()
  const navigate = useNavigate()
  const { authInfo } = useAuth()
  const { profile } = useProfileContext()
  const redirectState = isCompletionRedirectState(location.state)
    ? location.state
    : null
  const mode = redirectState?.mode ?? CompletionMode.ONBOARDING
  const paymentMode = mode === CompletionMode.PAYMENT
  const personalComplete = hasCompletePersonalDetails(profile)
  const termsComplete = hasAcceptedTerms(profile)
  const billingComplete = hasCompleteBillingAddress(profile?.address)
  const [stepOrder, setStepOrder] = useState<SetupStep[]>(() => [
    ...(!termsComplete ? [SetupStep.TERMS] : []),
    ...(!personalComplete ? [SetupStep.PERSONAL] : []),
    ...(!billingComplete ? [SetupStep.BILLING] : []),
  ])
  const [currentStep, setCurrentStep] = useState(1)
  const [skippedSteps, setSkippedSteps] = useState<number[]>([])

  const userEmail =
    getFromToken(authInfo?.accessToken ?? '', JwtKeys.EMAIL) ?? ''
  const steps = stepOrder.map(step => t(`account.setup.steps.${step}`))
  const activeStep = stepOrder[currentStep - 1]
  const canSkip = activeStep !== SetupStep.TERMS
  const isLastStep = currentStep === stepOrder.length

  const isStepDisabled = (step: number) => {
    const targetStep = stepOrder[step - 1]

    return (
      (stepOrder.includes(SetupStep.TERMS) &&
        targetStep !== SetupStep.TERMS) ||
      (paymentMode && step > currentStep)
    )
  }

  const goToStep = (step: number) => {
    if (isStepDisabled(step)) {
      return
    }

    setCurrentStep(step)
    setSkippedSteps(previous => previous.filter(item => item !== step))
  }

  const completeStep = (step: SetupStep) => {
    const remainingSteps = stepOrder.filter(item => item !== step)

    if (remainingSteps.length === 0) {
      navigate(redirectState?.returnTo ?? '/dashboard', {
        replace: true,
        state: redirectState?.returnState,
      })
      return
    }

    setSkippedSteps(previous =>
      previous.filter(skippedStep => skippedStep !== currentStep)
    )

    setStepOrder(remainingSteps)
    setCurrentStep(previous => Math.min(previous, remainingSteps.length))
  }

  const skipStep = () => {
    if (paymentMode) {
      navigate(redirectState?.returnTo ?? '/dashboard', {
        replace: true,
        state: redirectState?.cancelState,
      })
      return
    }

    setSkippedSteps(previous =>
      previous.includes(currentStep)
        ? previous
        : [...previous, currentStep]
    )

    if (isLastStep) {
      navigate(redirectState?.returnTo ?? '/dashboard', {
        replace: true,
        state: redirectState?.returnState,
      })
    } else {
      goToStep(currentStep + 1)
    }
  }

  const renderStep = () => {
    if (activeStep === SetupStep.PERSONAL) {
      return (
        <ProfileDetails
          profile={profile}
          userEmail={userEmail}
          requiredOnly
          onSaveSuccess={() => completeStep(SetupStep.PERSONAL)}
          onSkip={canSkip ? skipStep : undefined}
          saveLabel={t('account.setup.save')}
          skipLabel={t('account.setup.skip')}
        />
      )
    }

    if (activeStep === SetupStep.TERMS) {
      return (
        <TermsConsent
          saveLabel={t('account.setup.save')}
          onAccepted={() => completeStep(SetupStep.TERMS)}
        />
      )
    }

    return (
      <Billing
        profile={profile}
        requiredOnly
        onSaveSuccess={() => completeStep(SetupStep.BILLING)}
        onSkip={canSkip ? skipStep : undefined}
        saveLabel={t('account.setup.save')}
        skipLabel={t('account.setup.skip')}
      />
    )
  }

  if (stepOrder.length === 0) {
    return (
      <Navigate
        to={redirectState?.returnTo ?? '/dashboard'}
        replace
        state={redirectState?.returnState}
      />
    )
  }

  return (
    <div>
      <PageHeader
        title={{
          eyebrow: t(
            paymentMode
              ? 'account.setup.payment-eyebrow'
              : 'account.setup.onboarding-eyebrow'
          ),
          h1: t('account.setup.title'),
        }}
        sub={t(
          paymentMode
            ? 'account.setup.payment-subtitle'
            : 'account.setup.onboarding-subtitle'
        )}
      />

      <WizardLayout
        cardClassName="col-12 responsive-half-card"
        steps={steps}
        currentStep={currentStep}
        ariaLabel={t('account.setup.navigation')}
        skippedSteps={skippedSteps}
        skippedLabel={t('account.setup.skipped')}
        onStepChange={goToStep}
        isStepDisabled={isStepDisabled}
      >
        {renderStep()}
      </WizardLayout>
    </div>
  )
}

export default AccountSetup
